import React, { useEffect, useMemo, useState } from 'react'
import {
  useI18n,
  usePastDiagnosis,
  useRehabSelectedThemes,
} from 'contexts/AuthContext'
import { Button } from 'antd'
import notEligibleIcon from './img/ic-not-eligible.svg'
import { useUpdateClient } from 'hooks/careHooks'
import { getHealthieUserId, getLocalLanguage } from 'utils/authLocalStorage'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { updateUserPromise } from 'apis/FitOnCareAPI'
import { logBrazeEvent } from 'utils/brazeInit'
import ContentHeader from './ContentHeader'
import { canCloseToFitOnWeb, rehabCareFlowFinished } from './Rehab/util'
import { CareReason } from './Rehab/returnTo'

export const UhOhStatus = Object.freeze({
  PAIN_LOCATED: 1,
  QUESTION_1: 2,
  PAST_DIAGNOSIS: 3,
})

const UhOh = (props) => {
  const { i18n } = useI18n()
  const { sku, from, fitonUserId } = props
  const { updateClient } = useUpdateClient()
  const { pastDiagnosis } = usePastDiagnosis()

  const { rehabSelectedThemes } = useRehabSelectedThemes()

  useEffect(() => {
    props.setRemoveEventListener(true)
  }, [])

  useEffect(() => {
    const logNotAvailableEvent = (detail, theme) => {
      logAmplitudeEvent('Screen View: Onboarding - Not Available', {
        Reason: 'Condition',
        Detail: detail,
        sku,
        ...(theme && { theme }),
        language: getLocalLanguage(),
      })
    }

    switch (sku) {
      case 'pt':
        updateUserPromise({
          skuStatus: {
            sku,
            status: 5,
          },
        })
        logNotAvailableEvent(
          'No Pain',
          rehabSelectedThemes?.map((theme) => theme.name).join(',')
        )
        logBrazeEvent(`User Denied: No Pain - ${sku}`)
        break
      case 'msk':
        logNotAvailableEvent(
          'No Pain',
          rehabSelectedThemes?.map((theme) => theme.name).join(',')
        )
        logBrazeEvent(`User Denied: No Pain - ${sku}`)
        break
      case 'wlc':
        updateUserPromise({
          skuStatus: {
            sku,
            status: 5,
          },
        })
        const wlcDetail =
          pastDiagnosis[0]?.name === 'Other'
            ? `Other - ${pastDiagnosis[0].otherText}`
            : pastDiagnosis[0]?.name === 'None of the above'
              ? 'None of the above'
              : pastDiagnosis[0]?.name
        logNotAvailableEvent(wlcDetail)
        break
      default:
        break
    }
  }, [sku, pastDiagnosis, rehabSelectedThemes])

  const onClose = () => {
    switch (sku) {
      case 'wlc':
        updateClient({
          variables: {
            id: getHealthieUserId(),
            active: false,
          },
          onSuccess: () => {
            props.onClose()
          },
        })
        break
      default:
        props.onClose()
        break
    }
  }

  // CARE-160: on the web, a rehab member with no recent pain can go back to the FitOn page they came
  // from. The apps close the flow from the header instead.
  const showWebClose = canCloseToFitOnWeb({ sku, from })
  const onWebClose = () => {
    rehabCareFlowFinished(false, 0, from, fitonUserId, sku, CareReason.noPain)
  }

  const onBack = (e) => {
    if (sku === 'wlc') {
      updateUserPromise({ activate: 0 })
    }

    props.setRemoveEventListener(false)
    props.onBack(e)
  }

  const getDynamicText = useMemo(() => {
    switch (sku) {
      case 'wlc':
        return 'For this program, you must have a related condition. Please review your previous selection or take a standard FitOn workout.'
      default:
        return 'For this program, you must have experienced some level of pain, stiffness, or discomfort in your muscles, joints, or bones in the previous two weeks. Please review your previous selection or take a standard FitOn workout.'
    }
  }, [sku])

  return (
    <div className='info-sec'>
      <ContentHeader {...props} onBack={onBack} onClose={onClose} />
      <div className='info-content'>
        <img src={notEligibleIcon} alt='' className='top-icon' />
        <div className='title'>{i18n('Uh Oh!')}</div>
        <div
          className='subtitle'
          style={{ marginBottom: '10px', marginTop: '17px' }}
        >
          {i18n(getDynamicText)}
        </div>
        <Button className='btn-primary small-btn m-b-24' onClick={onBack}>
          {i18n('Edit Selection')}
        </Button>
        {showWebClose && (
          <Button
            className='btn-cancel small-btn font-dark m-t-0'
            onClick={onWebClose}
          >
            {i18n('Close')}
          </Button>
        )}
      </div>
    </div>
  )
}

export default UhOh
